import React, { useState, useEffect, useRef } from 'react';
import { sanitizeString } from '../../utils/sanitization';
import { useAuth } from '../auth/AuthContext';
import { collectionPath } from '../../utils/firestoreCollections';
import { hasPermission, FEATURE_PERMISSIONS } from '../../utils/permissions';

const MIN_CHARS = 2;

const ContactModal = ({
    open,
    contact,
    onClose,
    onSave,
    contactFocusOptions,
    persistNewContactFocus,
    customers,
}) => {
    const { currentUser } = useAuth();
    const [form, setForm] = useState(() => {
        const cf = contact?.contactFocus;
        return {
            ...(contact || {}),
            contactFocus: Array.isArray(cf)
                ? cf
                : (typeof cf === 'string' && cf ? cf.split(',').map(s => s.trim()).filter(Boolean) : []),
        };
    });
    const [showAddContactFocus, setShowAddContactFocus] = useState(false);
    const [newContactFocus, setNewContactFocus] = useState('');
    const [saving, setSaving] = useState(false);

    // Multiselect dropdown state
    const [focusDropdownOpen, setFocusDropdownOpen] = useState(false);
    const focusRef = useRef(null);

    useEffect(() => {
        // normalize incoming contact.contactFocus to array, handle objects and other types
        const cf = contact?.contactFocus;
        let normalized = [];
        if (Array.isArray(cf)) {
            normalized = cf;
        } else if (typeof cf === 'string' && cf) {
            normalized = cf.split(',').map(s => s.trim()).filter(Boolean);
        } else if (cf && typeof cf === 'object') {
            // If it's an object, try to extract values
            normalized = Object.values(cf).map(String).filter(Boolean);
        } else {
            normalized = [];
        }
        setForm({
            ...(contact || {}),
            contactFocus: normalized,
        });
    }, [contact]);

    // close dropdown on outside click
    useEffect(() => {
        function onDocClick(e) {
            if (focusRef.current && !focusRef.current.contains(e.target)) {
                setFocusDropdownOpen(false);
            }
        }
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    if (!open) return null;

    const toggleFocusValue = (value) => {
        setForm(prev => {
            let current = [];
            if (Array.isArray(prev.contactFocus)) {
                current = [...prev.contactFocus];
            } else if (typeof prev.contactFocus === 'string' && prev.contactFocus) {
                current = prev.contactFocus.split(',').map(s => s.trim()).filter(Boolean);
            } else if (prev.contactFocus && typeof prev.contactFocus === 'object') {
                current = Object.values(prev.contactFocus).map(String).filter(Boolean);
            }
            const idx = current.indexOf(value);
            if (idx === -1) current.push(value);
            else current.splice(idx, 1);
            return { ...prev, contactFocus: current };
        });
    };

    const handleAddFocusFromInput = async () => {
        const trimmed = (newContactFocus || '').trim();
        if (!trimmed) return;
        if (!contactFocusOptions.includes(trimmed)) {
            // persist and update via provided callback
            await persistNewContactFocus(trimmed, (val) => {
                // add to selected values
                setForm(prev => {
                    const current = Array.isArray(prev.contactFocus) ? [...prev.contactFocus] : [];
                    if (!current.includes(val)) current.push(val);
                    return { ...prev, contactFocus: current };
                });
            });
        } else {
            // just add to selection
            toggleFocusValue(trimmed);
        }
        setNewContactFocus('');
        setShowAddContactFocus(false);
        setFocusDropdownOpen(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <form
                onSubmit={async e => {
                    e.preventDefault();
                    if (!hasPermission(currentUser, FEATURE_PERMISSIONS.CONTACT_EDIT)) return;
                    setSaving(true);
                    
                    // Ensure contactFocus is always a clean array
                    let cleanContactFocus = [];
                    if (Array.isArray(form.contactFocus)) {
                        cleanContactFocus = form.contactFocus.map(v => sanitizeString(String(v), 50)).filter(Boolean);
                    } else if (typeof form.contactFocus === 'string' && form.contactFocus) {
                        cleanContactFocus = form.contactFocus.split(',').map(s => sanitizeString(s.trim(), 50)).filter(Boolean);
                    } else if (form.contactFocus && typeof form.contactFocus === 'object') {
                        cleanContactFocus = Object.values(form.contactFocus).map(v => sanitizeString(String(v), 50)).filter(Boolean);
                    }
                    
                    const safe = {
                        id: form.id,
                        firstName: sanitizeString(form.firstName, 100),
                        lastName: sanitizeString(form.lastName, 100),
                        email: sanitizeString(form.email, 200),
                        phone: sanitizeString(form.phone, 50),
                        title: sanitizeString(form.title, 200),
                        contactFocus: cleanContactFocus,
                        isPrimary: !!form.isPrimary,
                    };
                    
                    await onSave(safe);
                    setSaving(false);
                }}
                className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto space-y-4"
            >
                <h3 className="text-lg font-semibold mb-2">Edit Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">First Name <span className="text-red-600">*</span></label>
                        <input
                            className="w-full border p-2 rounded"
                            value={form.firstName || ''}
                            onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                            required
                        />
                        <label className="block text-sm font-medium mb-1 mt-2">Last Name <span className="text-red-600">*</span></label>
                        <input
                            className="w-full border p-2 rounded"
                            value={form.lastName || ''}
                            onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                            required
                        />
                        <label className="block text-sm font-medium mb-1 mt-2">Email</label>
                        <input
                            className="w-full border p-2 rounded"
                            value={form.email || ''}
                            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        />
                        <label className="block text-sm font-medium mb-1 mt-2">Phone</label>
                        <input
                            className="w-full border p-2 rounded"
                            value={form.phone || ''}
                            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                        />
                        <label className="block text-sm font-medium mb-1 mt-2">Title</label>
                        <input
                            className="w-full border p-2 rounded"
                            value={form.title || ''}
                            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                        />
                        <div className="flex items-center mt-2">
                            <input
                                type="checkbox"
                                id="isPrimary"
                                checked={!!form.isPrimary}
                                onChange={e => setForm(f => ({ ...f, isPrimary: e.target.checked }))}
                                className="mr-2"
                            />
                            <label htmlFor="isPrimary" className="text-sm">Primary Contact</label>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1 mt-2">Focus <span className="text-red-600">*</span></label>

                        <div className="relative" ref={focusRef}>
                            <button
                                type="button"
                                className="w-full text-left border p-2 rounded flex justify-between items-center"
                                onClick={() => setFocusDropdownOpen(open => !open)}
                            >
                                <span className="truncate">
                                    {(Array.isArray(form.contactFocus) && form.contactFocus.length > 0)
                                        ? form.contactFocus.join(', ')
                                        : 'Select Focus'}
                                </span>
                                <svg className="w-4 h-4 ml-2 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={focusDropdownOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                </svg>
                            </button>

                            {/* Dropdown panel with checkboxes + inline add area (stop propagation so outside click handler doesn't close it) */}
                            {focusDropdownOpen && (
                                <div
                                    className="absolute z-40 mt-2 w-full bg-white border rounded shadow max-h-[60vh] overflow-auto p-2"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="space-y-1">
                                        {contactFocusOptions.map(opt => (
                                            <label key={opt} className="flex items-center space-x-2 p-1 hover:bg-gray-50 rounded">
                                                <input
                                                    type="checkbox"
                                                    checked={Array.isArray(form.contactFocus) && form.contactFocus.includes(opt)}
                                                    onChange={() => toggleFocusValue(opt)}
                                                    className="form-checkbox"
                                                />
                                                <span className="text-sm">{opt}</span>
                                            </label>
                                        ))}
                                        <div className="border-t mt-2 pt-2">
                                            {showAddContactFocus ? (
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="text"
                                                        className="border p-2 rounded flex-1"
                                                        placeholder="New focus (e.g., Soccer)"
                                                        value={newContactFocus}
                                                        onChange={e => setNewContactFocus(e.target.value)}
                                                        autoFocus
                                                    />
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1 bg-indigo-600 text-white rounded"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            const trimmed = (newContactFocus || '').trim();
                                                            if (!trimmed) return;
                                                            if (trimmed && !contactFocusOptions.includes(trimmed)) {
                                                                await persistNewContactFocus(trimmed, (val) => {
                                                                    setForm(prev => {
                                                                        const current = Array.isArray(prev.contactFocus) ? [...prev.contactFocus] : [];
                                                                        if (!current.includes(val)) current.push(val);
                                                                        return { ...prev, contactFocus: current };
                                                                    });
                                                                });
                                                            } else if (trimmed) {
                                                                toggleFocusValue(trimmed);
                                                            }
                                                            setShowAddContactFocus(false);
                                                            setNewContactFocus('');
                                                            setFocusDropdownOpen(true);
                                                        }}
                                                    >
                                                        Add
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1 bg-gray-200 rounded"
                                                        onClick={(e) => { e.stopPropagation(); setShowAddContactFocus(false); setNewContactFocus(''); }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="w-full text-left text-sm text-indigo-600 hover:underline"
                                                    onClick={(e) => { e.stopPropagation(); setShowAddContactFocus(true); setFocusDropdownOpen(true); }}
                                                >
                                                    + Add New Focus
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                     </div>
                 </div>

                <div className="flex justify-end space-x-2 mt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 rounded"
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 bg-indigo-600 text-white rounded"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ContactModal;